import { Dialog, DialogContent, DialogTitle, Grid } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { memo, useMemo } from 'react';

import FormActionButtons from 'components/ui/form/FormActionButtons';
import FormCurrencyField from 'components/ui/form/FormCurrencyField';
import FormTextField from 'components/ui/form/FormTextField';
import FormSelectField from 'components/ui/form/FormSelectField';
import FormDateField from 'components/ui/form/FormDateField';
import useFormSubmit from 'hooks/useFormSubmit';

const toToday = () => new Date().toISOString().slice(0, 10);

// Bo'sh string bo'lsa -> undefined qilib yuboramiz (yup.number uchun)
const numberRequired = (msg) =>
  yup
    .number()
    .transform((val, orig) => (orig === '' || orig === null ? undefined : val))
    .typeError(msg)
    .required(msg);

const validationSchema = yup.object({
  input: numberRequired("Boshlang'ich valyuta majburiy!"),
  output: numberRequired('Maqsad valyuta majburiy!'),
  type: numberRequired('Konvertatsiya shakli majburiy!'),
  date: yup
    .string()
    .required('Sana majburiy!'),
  comment: yup.string().nullable(),
});

const buildInitialValues = (data) => {
  if (data?.id) {
    return {
      input: Number(data?.inputsum) || Number(data?.inputval) || 0,
      output: Number(data?.outputsum) || Number(data?.outputval) || 0,
      // inputsum + outputval bo'lsa 1, aks holda 2 (o'zingizdagi mantiq saqlandi)
      type: Number(data?.inputsum) && Number(data?.outputval) ? 1 : 2,
      comment: data?.description || '',
      date: data?.date || toToday(),
    };
  }
  return {
    input: '',
    output: '',
    type: '',
    comment: '',
    date: toToday(),
  };
};

const ConversionModal = (props) => {
  const { open, setOpen, setRefetch, data, setData, accountId } = props;
  const { submit, isSubmitting } = useFormSubmit();

  const initialValues = useMemo(() => buildInitialValues(data), [data]);

  const handleFinishRequest = () => {
    setRefetch(true);
    handleClose();
    setData({});
  };

  const handleClose = () => {
    setOpen(false);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      // URL ishlab chiqish: post vs postUpdate
      if (data?.id) {
        // Eslatma: useFormSubmit ichida postUpdate bo‘lganda /update/:id qo‘shilsa,
        // bu yerda URLga FAQAT resursning bazaviy yo‘lini yuboramiz.
        // Aksincha bo‘lsa, pastdagini moslang.
        submit(
          { type: 'postUpdate', contentType: 'simple' },
          values,
          `admin/accounts/conversion`, // <-- bu yerda update/.. qo‘shmadiq
          undefined,                   // values.name yo'q; undefined yuboramiz
          data.id,                     // postUpdate uchun id alohida beriladi
          true,
          handleFinishRequest
        );
      } else {
        submit(
          { type: 'post', contentType: 'simple' },
          values,
          `admin/accounts/conversion/${accountId}`, // create uchun accountId bilan
          undefined,
          accountId,
          true,
          handleFinishRequest
        );
      }
    },
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      maxWidth='md'
      PaperProps={{
        className: '!base-border !shadow-none',
      }}
      disableEscapeKeyDown={true}
    >
      <DialogTitle
        id='alert-dialog-title'
        className='!text-center !bg-gray-100 !mb-2'
      >
        <span>Pulni konvertatsiya qilish</span>
      </DialogTitle>

      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <Grid
            container
            spacing={{ xs: 2, sm: 3, lg: 3 }}
            rowSpacing={1}
            columns={{ xs: 12, sm: 12, lg: 12 }}
          >
            <Grid item lg={12} sm={12} xs={12}>
              <FormSelectField
                delay={0}
                label={'Konvertatsiya shakli'}
                fieldName={'type'}
                formik={formik}
                path={'/dictionary/conversationtypes'} // <-- typo tuzatildi
              />
            </Grid>

            <Grid item lg={6} sm={6} xs={12}>
              <FormDateField
                delay={0}
                label={'Sanasi'}
                fieldName='date'
                formik={formik}
              />
            </Grid>

            <Grid item lg={6} sm={6} xs={12}>
              <FormCurrencyField
                delay={0.1}
                label={'Chiqarish summasi'}
                fieldName='output'
                formik={formik}
              />
            </Grid>

            <Grid item lg={6} sm={6} xs={12}>
              <FormCurrencyField
                delay={0.1}
                label={'Kiritish summasi'}
                fieldName='input'
                formik={formik}
              />
            </Grid>

            <Grid item lg={12} sm={12} xs={12}>
              <FormTextField
                delay={0.1}
                label={'Izoh'}
                fieldName='comment'
                rows={4}
                multiline
                formik={formik}
              />
            </Grid>

            <Grid item sm={12} xs={12}>
              <FormActionButtons
                delay={0.4}
                isSubmitting={isSubmitting}
                formType='dialog'
                setOpen={setOpen}
                reset={formik.resetForm}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default memo(ConversionModal);